/**
 * DefaultPageChatSurface — registers the dock's default "page" chat floor and
 * renders its LOCAL body into the dock via <DockSlot>.
 *
 * Mounted once in AppRouter. On every authed page that registers no specific
 * surface, this provides page-context-aware chat in the dock (the role the
 * floating button used to play). The descriptor is `external: true` AND
 * `isDefault: true`, so any specific surface (email/notes/studio/meals/home)
 * outranks it (see the registry) and the floor mounts lazily — DockSlot portals
 * the body only while the dock is open, so the engine does not spin up on every
 * page load. Suppressed on /chat (the chat page owns its own surface).
 */
import React, { lazy, Suspense } from 'react';

import { useLocation } from 'react-router-dom';

import { DockSlot } from '@shared/components/chat/dock/DockSlot';
import { useRegisterChatSurface } from '@shared/components/chat/dock/useRegisterChatSurface';
import LoadingSpinner from '@shared/components/feedback/LoadingSpinner';

import { DEFAULT_PAGE_DOCK_OWNER_ID, useDefaultPageChatDescriptor } from './useDefaultPageChatDescriptor';

/**
 * Code-split out of the app shell.
 *
 * This surface mounts on every authed page, and the body behind it pulls the
 * whole chat stack — the engine, the attachment pipeline (heic2any, and the
 * libheif payload it carries), the markdown renderers. DockSlot already defers
 * *mounting* it until the dock opens; importing it lazily is what keeps those
 * bytes out of the entry chunk too.
 */
const DefaultPageChatBody = lazy(() =>
  import('./DefaultPageChatBody').then(m => ({ default: m.DefaultPageChatBody })),
);

const DefaultPageChatSurface: React.FC = () => {
  const { pathname } = useLocation();
  const onChatRoute = pathname.startsWith('/chat');

  const descriptor = useDefaultPageChatDescriptor(pathname);
  // Register the floor on every authed page EXCEPT /chat (same gating as before).
  useRegisterChatSurface(onChatRoute ? null : descriptor, [onChatRoute, descriptor]);

  // The floor body is portaled in by DockSlot only while the floor is the active
  // surface AND the dock is open (lazy, via isDefault + the bodyOpen gate). Don't
  // render the body on /chat — the dock is hidden there.
  return (
    <DockSlot ownerId={DEFAULT_PAGE_DOCK_OWNER_ID}>
      {onChatRoute ? null : (
        <Suspense fallback={<LoadingSpinner className="h-full" message="" />}>
          <DefaultPageChatBody pathname={pathname} />
        </Suspense>
      )}
    </DockSlot>
  );
};

export default DefaultPageChatSurface;
